import { useEffect, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.vectorgrid";
import {
  COLOR,
  COMBINED_LEVELS_FILTER,
  defaultPosition,
  MAP_VIEW,
} from "../../utils/Constants.tsx";
import { LegendCountryValuesType } from "../../utils/Types.tsx";
import { getColorCode, getThresholds, sumAll } from "../../utils/Util.tsx";
import { paths } from "../../services/ServiceConstants.tsx";

/**
 * This method returns color code to fill the color on Map.
 * thresholds values are used to find the color code.
 * @param sid it is state id
 * @param filterName it is
 * @param idWiseData this is key value pair. It store state id and respective state information
 * @returns it return color code
 */
const getColorBySid = (
  sid: number,
  filterName: string,
  idWiseData: any,
  legendValues: LegendCountryValuesType,
) => {
  if (!idWiseData) {
    return COLOR.white;
  }
  const dataItem = idWiseData[sid as keyof any];
  if (!dataItem) {
    return COLOR.white;
  }
  const thresholds = getThresholds(
    dataItem.levelOne,
    dataItem.levelTwo,
    legendValues,
  );

  const values = Number(dataItem?.total || 0);
  if(filterName === COMBINED_LEVELS_FILTER.Claims_Loss_Reserve && values === 0) {
    return COLOR.country.min;
  }
  if (
    (filterName === COMBINED_LEVELS_FILTER.Claims_Rejected ||
      COMBINED_LEVELS_FILTER.Claims_Repudiated ||
      COMBINED_LEVELS_FILTER.Claims_Cwp) &&
    values === 0
  ) {
    return COLOR.country.max;
  }
  if (!values || !thresholds.minMax) {
    console.log(values, "gfjh");

    return COLOR.white;
  }
  const code = getColorCode(
    values,
    thresholds?.minMax.min,
    thresholds?.minMax.max,
    COLOR.country.min,
    COLOR.country.max,
  );
  return code;
};

const findColorByGridId = (
  idWiseData: any,
  legendValues: any,
  filterName: any,
) => {
  const ids = Object.keys(idWiseData);
  const gridColor = {};
  ids.forEach((gridid) => {
    const data = idWiseData[gridid];
    let thresholds = getThresholds(
      data?.levelOne,
      data?.levelTwo,
      legendValues,
    );
    let dataKey = thresholds.key;
    let value: number = 0;
    if (dataKey === "payout_amounts") {
      value = sumAll(data[dataKey]);
    } else if (dataKey) {
      value = data[dataKey];
    }
    if (
      (filterName === COMBINED_LEVELS_FILTER.Claims_Rejected ||
        COMBINED_LEVELS_FILTER.Claims_Repudiated ||
        COMBINED_LEVELS_FILTER.Claims_Cwp) &&
      value === 0
    ) {
      gridColor[gridid] = { color: COLOR.country.max, hasBorder: false };
    } else if (!value || !thresholds?.minMax) {
      gridColor[gridid] = { color: COLOR.white, hasBorder: false };
    } else {
      const code = getColorCode(
        value,
        thresholds?.minMax.min,
        thresholds?.minMax.max,
        COLOR.country.min,
        COLOR.country.max,
      );
      gridColor[gridid] = { color: code, hasBorder: false };
    }
  });

  return gridColor;
};
const getFillData = (properties, gridColor) => {
  if (
    properties?.gridid &&
    gridColor &&
    gridColor[properties?.gridid] &&
    gridColor[properties?.gridid]?.color
  ) {
    return gridColor[properties?.gridid];
  }
  return { color: COLOR.white, hasValue: false };
};

const CountryVectorLayer = ({
  filterName,
  idWiseData,
  legendValues,
  mapView,
  setCountryVectorGridLayer,
  setCountryStateVectorLayer,
  setMapClickedInfo,
  selectedGeometry,
  gridCenter,
  clearLayers,
}) => {
  const [gridColor, setGridColor] = useState(null);
  const [selectorPolygon, setSelectorPolygon] = useState(null);
  const map = useMap();
  /**
   * Get vector tile layer styles for mbtile layers
   * @returns
   */
  const getVectorTileLayerStyles = (mapViewType) => {
    console.log(mapViewType, "mapview");

    if (mapViewType === MAP_VIEW.GRID_WISE) {
      return {
        grid_april14: (properties, zoom) => {
          const fillData = getFillData(properties, gridColor);
          return {
            fill: true,
            fillColor: fillData.color,
            fillOpacity: 1,
            stroke: false, // fillData.hasBorder,
            color: COLOR.transparent, //COLOR.black,
            weight: 1,
          };
        },
      };
    } else {
      return {
        stateinfo: (properties, zoom) => {
          return {
            fill: true,
            fillColor:
              mapView === MAP_VIEW.STATE_WISE
                ? getColorBySid(
                    properties?.sid,
                    filterName,
                    idWiseData,
                    legendValues,
                  )
                : COLOR.transparent,
            fillOpacity: 1,
            stroke: true,
            color: COLOR.black,
            weight: 1,
          };
        },
      };
    }
  };

  const addSelector = () => {
    try {
      if (selectorPolygon) {
        selectorPolygon.remove();
        setSelectorPolygon(null);
      }
      if (selectedGeometry) {
        // onSelectorAdded(false);
        console.log(selectedGeometry, "geom");

        const polygon = L.polygon(selectedGeometry);
        polygon.addTo(map);
        setSelectorPolygon(polygon);
      }
    } catch (error) {
      console.log(error, "err");
    }
  };
  /**
   * Add vector tile layers
   */
  const loadTileLayer = (mapViewType) => {
    if (mapViewType) {
      const fetchOptions = {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("AUTH_TOKEN"),
        },
      };

      const vectorTileLayer = L.vectorGrid
        .protobuf(
          mapViewType === MAP_VIEW.STATE_WISE
            ? paths.COUNTRY_STATE_MBTILE_URL
            : paths.COUNTRY_GRID_MBTILE_URL,
          {
            interactive: mapViewType === MAP_VIEW.STATE_WISE ? false : true,
            fetchOptions,
            getFeatureId: function (feature) {
              return feature.properties.id;
            },
            vectorTileLayerStyles: getVectorTileLayerStyles(mapViewType),
          },
        )
        .addTo(map);
      if (mapViewType === MAP_VIEW.GRID_WISE) {
        setCountryVectorGridLayer(vectorTileLayer);
      } else if (mapViewType === MAP_VIEW.STATE_WISE) {
        setCountryStateVectorLayer(vectorTileLayer);
      }

      vectorTileLayer.on("load", function (e) {});

      vectorTileLayer.on("loading", function (e) {});

      vectorTileLayer.on("error", function (e) {});
      vectorTileLayer.on("click", (e) => {
        setMapClickedInfo({
          lat: e?.latlng?.lat,
          lng: e?.latlng?.lng,
          gridid: e?.layer?.properties.gridid,
        });
      });
    }
  };

  useEffect(() => {
    if (map && gridCenter) {
      map.setView(gridCenter);
      const timer = setTimeout(() => {
        map.setZoom(10);
      }, 400);
      return () => clearTimeout(timer);
    } else if (map && !gridCenter) {
      map.setView(defaultPosition);
      const timer = setTimeout(() => {
        map.setZoom(5);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [gridCenter]);

  useEffect(() => {
    if (idWiseData && mapView === MAP_VIEW.STATE_WISE) {
      setGridColor(null);
      clearLayers(map);
      loadTileLayer(mapView);
      map.setView(defaultPosition);
      map.setZoom(5);
    } else if (idWiseData && mapView === MAP_VIEW.GRID_WISE) {
      setGridColor(findColorByGridId(idWiseData, legendValues, filterName));
    }
  }, [map, idWiseData, mapView]);

  useEffect(() => {
    if (gridColor && mapView === MAP_VIEW.GRID_WISE) {
      clearLayers(map);
      loadTileLayer(mapView);
      loadTileLayer(MAP_VIEW.STATE_WISE);
      map.setView(defaultPosition);
      map.setZoom(5);
    }
  }, [gridColor]);

  useEffect(() => {
    addSelector();
  }, [selectedGeometry]);
};

export default CountryVectorLayer;
